/**
 * Ayuda en pantalla (spec 17). Cada pantalla, pestaña y ventana compleja tiene su tema: para qué sirve, cómo se hace
 * cada tarea, consejos y qué hacer con sus mensajes. El texto sale del manual de usuario (docs/manual-usuario.md) y
 * sigue su estilo: frases cortas, de tú, los nombres de botones y campos en **negrita** tal como salen en pantalla.
 */
import type { Nivel, Permiso } from '@mg/shared';

/**
 * Quién ve el tema o el bloque: con el permiso (cualquier nivel salvo que se diga otro). Sin esto, todos.
 * Con `sin: true` es al revés: lo ve solo quien NO tiene el permiso. Sirve para dar dos versiones de un mismo punto, por
 * ejemplo la regla del cobro con la tasa 2 para la dueña (`tasas.actualizar`) y la versión sin tasa para los demás
 * (pedido de la dueña 2026-10-19).
 */
export interface PermisoAyuda {
  clave: Permiso;
  nivel?: Nivel;
  sin?: boolean;
}

/**
 * La tasa 2, el factor y la regla del cobro («a tasa 2…», «a tasa BCV… + IGTF», el tope de la nota) solo los ve la dueña
 * (pedido de la dueña 2026-10-19). En la ayuda: `VE_TASA_2` para el punto con la tasa 2 o la regla, y `SIN_TASA_2` para
 * su versión sin tasa («Si paga en bolívares: Bs …; si paga en dólares: $ … con IGTF»). Se decide con «Tasas del día»
 * (`tasas.actualizar`), que en la plantilla solo tiene la dueña.
 */
export const VE_TASA_2: PermisoAyuda = { clave: 'tasas.actualizar' };
export const SIN_TASA_2: PermisoAyuda = { clave: 'tasas.actualizar', sin: true };

export type BloqueAyuda =
  /** Una tarea paso a paso: «Cobrar una venta». */
  | { tipo: 'pasos'; titulo: string; pasos: string[]; permiso?: PermisoAyuda }
  /** Puntos sueltos: qué muestra la pantalla, reglas, consejos. */
  | { tipo: 'lista'; titulo: string; items: string[]; permiso?: PermisoAyuda }
  /** Un aviso corto destacado. `aviso` para lo que hay que cuidar (algo que no se deshace, una regla fiscal). */
  | { tipo: 'nota'; texto: string; tono?: 'info' | 'aviso'; permiso?: PermisoAyuda };

export interface TemaAyuda {
  /** Único: «pos», «pos.cobro», «inventario.conteos». Un subtema lleva el id del padre antes del punto. */
  id: string;
  titulo: string;
  /** Tema padre (pestaña o ventana de una pantalla). */
  padre?: string;
  /**
   * Rutas de la pantalla, con los parámetros como en el enrutador («/documentos/:id»). La ayuda que se abre con el botón
   * es la del tema cuya ruta coincide con la pantalla actual. Solo los temas principales llevan ruta.
   */
  rutas?: string[];
  permiso?: PermisoAyuda;
  /** Para qué sirve, en una o dos frases. */
  resumen: string;
  bloques: BloqueAyuda[];
  /** Mensajes que puede dar esta pantalla y qué hacer (del manual, sección 32). */
  mensajes?: { mensaje: string; que_hacer: string }[];
  /** Sección del manual: «4.7». */
  manual?: string;
  /** Otros temas que conviene leer. */
  relacionados?: string[];
  /** Palabras con que la busca la gente («vuelto», «IGTF», «tasa 2»), para el buscador. */
  claves?: string[];
}
